import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";

export default function Navbar() {
  const [open, setOpen]         = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const loc = useLocation();
  const isAdmin   = loc.pathname.startsWith("/admin");
  const isManager = loc.pathname.startsWith("/manage");

  useEffect(() => {
    const handler = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handler);
    return () => window.removeEventListener("scroll", handler);
  }, []);

  // Close menu on route change
  useEffect(() => { setOpen(false); }, [loc.pathname]);

  if (isAdmin) return (
    <nav style={{background:"#050508", borderBottom:"1px solid rgba(255,255,255,0.08)"}}
      className="px-4 md:px-6 py-3 flex items-center justify-between sticky top-0 z-50">
      <div className="flex items-center gap-2">
        <div className="w-7 h-7 rounded bg-brand-600 flex items-center justify-center text-xs font-bold text-white">A</div>
        <span className="font-semibold text-sm text-white">NagNail Admin</span>
        <span className="text-xs bg-brand-900 text-brand-300 px-2 py-0.5 rounded-full border border-brand-700 hidden sm:inline">Tickets</span>
      </div>
      <div className="flex items-center gap-3 text-sm">
        <Link to="/admin/tickets" className="text-gray-300 hover:text-white transition text-xs md:text-sm">Dashboard</Link>
        <Link to="/" className="text-xs text-gray-500 hover:text-gray-300 transition">← Public</Link>
      </div>
    </nav>
  );

  if (isManager) return (
    <nav style={{background:"#050508", borderBottom:"1px solid rgba(255,255,255,0.08)"}}
      className="px-4 md:px-6 py-3 flex items-center justify-between sticky top-0 z-50">
      <div className="flex items-center gap-2">
        <div className="w-7 h-7 rounded bg-yellow-600 flex items-center justify-center text-xs font-bold text-black">M</div>
        <span className="font-semibold text-sm text-white">Event Manager</span>
      </div>
      <div className="flex items-center gap-3 text-sm">
        <Link to="/manage" className="text-gray-300 hover:text-white transition text-xs md:text-sm">Dashboard</Link>
        <Link to="/" className="text-xs text-gray-500 hover:text-gray-300 transition">← Public</Link>
      </div>
    </nav>
  );

  return (
    <>
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? "py-2 md:py-3" : "py-3 md:py-4"
      }`} style={{
        background: scrolled || open ? "rgba(7,0,15,0.97)" : "rgba(7,0,15,0.3)",
        backdropFilter: "blur(20px)",
        borderBottom: scrolled ? "1px solid rgba(255,255,255,0.06)" : "none",
      }}>
        <div className="max-w-7xl mx-auto px-4 md:px-6 flex items-center justify-between gap-4">

          {/* Logo */}
          <Link to="/" className="flex-shrink-0">
            <img src="/logos/logo-main.png" alt="NagNail Tickets"
              style={{height: scrolled ? "44px" : "52px", width:"auto", transition:"height 0.3s"}}
              className="md:h-auto"
            />
          </Link>

          {/* Desktop nav */}
          <div className="hidden md:flex items-center gap-8 flex-1 justify-center">
            {[{to:"/",label:"Events"},{to:"/my-tickets",label:"My Tickets"}].map(l => (
              <Link key={l.to} to={l.to}
                className={`text-sm font-medium transition-all relative group ${
                  loc.pathname === l.to ? "text-white" : "text-gray-400 hover:text-white"
                }`}>
                {l.label}
                <span className={`absolute -bottom-1 left-0 h-px bg-brand-500 transition-all duration-200 ${
                  loc.pathname === l.to ? "w-full" : "w-0 group-hover:w-full"
                }`}/>
              </Link>
            ))}
          </div>

          {/* Desktop CTA */}
          <div className="hidden md:flex items-center gap-3 flex-shrink-0">
            <Link to="/organiser-login"
              className="text-sm text-gray-300 hover:text-white transition px-4 py-2 rounded-xl"
              style={{border:"1px solid rgba(255,255,255,0.1)"}}>
              Organiser Login
            </Link>
            <Link to="/"
              className="text-sm font-bold text-white px-5 py-2.5 rounded-xl transition hover:scale-105"
              style={{background:"linear-gradient(135deg,#7c22c8,#9b3fe8)"}}>
              Book Now
            </Link>
          </div>

          {/* Mobile: My Tickets + Hamburger */}
          <div className="md:hidden flex items-center gap-2">
            <Link to="/my-tickets"
              className="text-xs text-gray-300 px-3 py-1.5 rounded-lg glass">
              My Tickets
            </Link>
            <button className="text-gray-300 p-2 rounded-lg glass" onClick={() => setOpen(!open)}>
              {open ? (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12"/>
                </svg>
              ) : (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16"/>
                </svg>
              )}
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile menu */}
      <div className={`fixed inset-0 z-40 transition-all duration-300 md:hidden ${
        open ? "opacity-100 visible pointer-events-auto" : "opacity-0 invisible pointer-events-none"
      }`} style={{background:"rgba(7,0,15,0.98)", backdropFilter:"blur(20px)", paddingTop:"80px"}}>
        <div className="flex flex-col p-6 gap-2">
          {[
            {to:"/",          label:"🎪 Events"},
            {to:"/my-tickets",label:"🎟️ My Tickets"},
            {to:"/organiser-login", label:"👤 Organiser Login"},
          ].map(l => (
            <Link key={l.to} to={l.to} onClick={() => setOpen(false)}
              className="flex items-center gap-3 px-5 py-4 rounded-2xl text-white font-semibold text-lg glass hover:bg-white/10 transition">
              {l.label}
            </Link>
          ))}

          {/* Book Now mobile */}
          <Link to="/" onClick={() => setOpen(false)}
            className="mt-4 text-center py-4 rounded-2xl text-white font-bold text-lg transition"
            style={{background:"linear-gradient(135deg,#7c22c8,#9b3fe8)"}}>
            Book Now →
          </Link>
        </div>
      </div>
    </>
  );
}
